/** 浏览器草稿只保存文本和 Host 不透明附件引用；File/本机路径仅在当前页面内保留。 */
export interface DraftAttachment { path: string; name: string; size?: number; file?: File }
export interface ConversationDraft { text: string; attachments: DraftAttachment[]; lostAttachments: number; updatedAt: number }
type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const EMPTY: ConversationDraft = { text: '', attachments: [], lostAttachments: 0, updatedAt: 0 };
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
/** 键不包含令牌；平台、用户、会话与新建任务执行端都隔离。 */
export function conversationDraftKey(platform: string, userId: string, sessionId?: string, target = 'cloud'): string {
  const session = !sessionId || sessionId.startsWith('temp-') ? 'new' : sessionId;
  return `bomate:composer:v1:${JSON.stringify([platform, userId, session, session === 'new' ? target : 'session'])}`;
}
/** 校验可跨刷新使用的附件引用，不持久化签名 URL、任意服务器路径或浏览器 File。 */
function durableAttachment(item: DraftAttachment): boolean {
  return typeof item.path === 'string' && /^hostinput:[A-Za-z0-9._:-]{1,128}\/input:v1:[A-Za-z0-9_-]{1,128}\/[^\r\n]{1,255}$/.test(item.path)
    && typeof item.name === 'string' && item.name.length <= 255;
}
/** 可注入 Storage 和时钟做隔离/到期/失败恢复测试；内存提供稳定快照。 */
export function createConversationDraftStore(storage: StorageLike, now = Date.now) {
  const cache = new Map<string, ConversationDraft>();
  const listeners = new Set<(key: string) => void>();
  /** 读取并验证草稿；损坏/过期记录不影响输入框。 */
  const read = (key: string): ConversationDraft => {
    const cached = cache.get(key);
    if (cached) return cached;
    let value = EMPTY;
    try {
      const raw = JSON.parse(storage.getItem(key) || 'null');
      if (raw && typeof raw.text === 'string' && raw.text.length <= 200_000 && Array.isArray(raw.attachments)
        && typeof raw.updatedAt === 'number' && now() - raw.updatedAt < MAX_AGE_MS && raw.updatedAt <= now()) {
        value = { text: raw.text, attachments: raw.attachments.filter(durableAttachment).slice(0, 50),
          lostAttachments: Number.isSafeInteger(raw.lostAttachments) && raw.lostAttachments > 0 ? raw.lostAttachments : 0,
          updatedAt: raw.updatedAt };
      }
    } catch { /* 损坏记录和浏览器禁用存储均使用空草稿。 */ }
    cache.set(key, value); return value;
  };
  return {
    read,
    /** 字符/附件变化即保存；存储失败保留内存内容，不截断用户输入。 */
    write(key: string, text: string, attachments: DraftAttachment[]): void {
      const previous = read(key);
      if (previous.text === text && previous.attachments === attachments) return;
      const value = { text, attachments, lostAttachments: previous.lostAttachments, updatedAt: now() };
      cache.set(key, value);
      try {
        const durable = attachments.filter(durableAttachment).map(({ path, name, size }) => ({ path, name, size }));
        if (text.length <= 200_000) storage.setItem(key, JSON.stringify({ ...value, attachments: durable,
          lostAttachments: value.lostAttachments + attachments.length - durable.length }));
        else storage.removeItem(key);
      } catch { /* 当前页仍保留草稿。 */ }
      for (const listener of listeners) listener(key);
    },
    /** 已确认发起/入队后仅清除原快照，用户随后输入的新草稿不能被迟到响应删除。 */
    clearIfUnchanged(key: string, text: string, attachments: DraftAttachment[]): void {
      const current = read(key);
      if (current.text !== text || current.attachments !== attachments) return;
      cache.set(key, EMPTY);
      try { storage.removeItem(key); } catch { /* 不影响已发送结果。 */ }
      for (const listener of listeners) listener(key);
    },
    /** 每个输入框只响应自身键的变化，支持发送时发生组件切换。 */
    subscribe(listener: (key: string) => void): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; },
  };
}
export const conversationDrafts = createConversationDraftStore({
  getItem: key => localStorage.getItem(key), setItem: (key, value) => localStorage.setItem(key, value), removeItem: key => localStorage.removeItem(key),
});
